import { OnboardingLayout } from '@/components/onboarding/OnboardingLayout';
import { ProfileConfig } from '@/components/user_configuration/user_profile/UserProfileConfig';
import { TravelConfig } from '@/components/user_configuration/user_billing/UserTravelConfig';
import { UserExpenseConfig } from '@/components/user_configuration/user_billing/UserExpenseConfig';
import { InvoiceConfig } from '@/components/user_configuration/user_invoice/UserInvoiceConfig';
import { RolesConfig, type RolesConfigHandle } from '@/components/user_configuration/user_roles/UserRolesConfig';
import { MultipliersConfig, type MultipliersConfigHandle } from '@/components/user_configuration/user_multipliers/UserMultipliersConfig';
import { CompletionStep } from '@/components/onboarding/CompletionStep';
import { UserSurchargeConfig } from '@/components/user_configuration';
import { BreakTimePolicySection } from '@/components/config_forms/breaks/BreakTimePolicySection';
import { UserConfigProvider as OnboardingConfigProvider, useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';
import { OnboardingWizardProvider, useOnboardingWizard } from '@/context/OnboardingWizardContext';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useState, useRef, useEffect } from 'react';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { ChevronDown, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';

const OnboardingContent = () => {
  const state = useUserConfigState();
  const { updateBillingConfig } = useUserConfigDispatch();
  const { currentStep } = useOnboardingWizard();

  const multipliersRef = useRef<MultipliersConfigHandle>(null);
  const rolesRef       = useRef<RolesConfigHandle>(null);
  const [multipliersStepValid, setMultipliersStepValid] = useState(true);
  const [rolesStepValid,       setRolesStepValid]       = useState(true);

  // Surcharge section accordion
  const [surchargesOpen, setSurchargesOpen] = useState(false);
  const surchargesHeaderRef = useRef<HTMLButtonElement>(null);

  const enabledSurcharges: string[] = [];
  if (state.usesShortTurn) enabledSurcharges.push('Short Turn');
  if (state.mealConfig?.applyMealSurcharge) enabledSurcharges.push('Meal Break');
  if (state.applyHolidayPremium) enabledSurcharges.push('Holiday');
  if (state.overnightConfig?.applyOvernightPremium) enabledSurcharges.push('Overnight');

  useEffect(() => {
    if (surchargesOpen && surchargesHeaderRef.current) {
      surchargesHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [surchargesOpen]);

  // Auto-expand surcharges when any surcharge is already enabled (e.g. from seed data)
  useEffect(() => {
    if (enabledSurcharges.length > 0) setSurchargesOpen(true);
  }, [enabledSurcharges.length]);

  const steps = [
    'Crew Info',
    'Rate Sheet',
    'Breaks, Overtime & Surcharges',
    'Travel & Expenses',
    'Invoicing',
    'Pre-Show Check'
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 0: // Crew Info
        return (
          <ProfileConfig
            firstName={state.firstName}
            lastName={state.lastName}
            email={state.email}
            phone={state.phone}
            isOnboarding
          />
        );
      case 1: // Rate Sheet (merged: roles + billing structure)
        return (
          <RolesConfig
            ref={rolesRef}
            onValidityChange={setRolesStepValid}
            dayRateSettingsCollapsible
          />
        );
      case 2: // Overtime & Surcharges (merged: multipliers + all surcharges)
        return (
          <>
            <MultipliersConfig
              ref={multipliersRef}
              onValidityChange={setMultipliersStepValid}
            />
            <div className="mt-6">
              <BreakTimePolicySection
                breakBillingPolicy={state.mealConfig?.breakBillingPolicy}
                onBreakBillingPolicyChange={(policy) => updateBillingConfig({ mealConfig: { ...state.mealConfig, breakBillingPolicy: policy } })}
                noCard
              />
            </div>
            <div className="mt-6">
              <button
                ref={surchargesHeaderRef}
                type="button"
                onClick={() => setSurchargesOpen(prev => !prev)}
                className="w-full flex items-center justify-between py-3 px-1"
              >
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span className="heading-label text-sm text-foreground">
                    Surcharges & Premiums
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {!surchargesOpen && enabledSurcharges.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap">
                      {enabledSurcharges.map(name => (
                        <span key={name} className="text-xs px-2 py-0.5 rounded bg-surface-2 text-primary border border-border">
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                  {!surchargesOpen && enabledSurcharges.length === 0 && (
                    <span className="text-xs text-muted-foreground">optional</span>
                  )}
                  <ChevronDown className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform duration-200",
                    surchargesOpen && "rotate-180"
                  )} />
                </div>
              </button>
              <AnimatedCollapse open={surchargesOpen}>
                    <div className="max-w-3xl pt-2">
                      <UserSurchargeConfig noCard />
                    </div>
              </AnimatedCollapse>
            </div>
          </>
        );
      case 3: // Travel & Expenses
        return (
          <>
            <TravelConfig noCard />
            <div className="mt-6">
              <UserExpenseConfig />
            </div>
          </>
        );
      case 4: // Invoicing
        return (
          <InvoiceConfig
            paymentTermsDays={state.paymentTermsDays}
            lateFeePercentage={state.lateFeePercentage}
            lateFeeFixedAmount={state.lateFeeFixedAmount}
            recurringLateFeeInterval={state.recurringLateFeeInterval}
            recurringLateFee={state.recurringLateFee}
            earlyPaymentDiscountPercentage={state.earlyPaymentDiscountPercentage}
            earlyPaymentFixedAmount={state.earlyPaymentFixedAmount}
            earlyPaymentDays={state.earlyPaymentDays}
            useLateFee={state.useLateFee}
            lateFeeType={state.lateFeeType}
            useEarlyPaymentDiscount={state.useEarlyPaymentDiscount}
            earlyPaymentType={state.earlyPaymentType}
            autoSendReminders={state.autoSendReminders}
            employmentType={state.employmentType}
            reminderDays={state.reminderDays}
            useCancellationPolicy={state.useCancellationPolicy}
            cancellationPolicy={state.cancellationPolicy}
            rushPremiumPolicy={state.rushPremiumPolicy}
          />
        );
      case 5: // Pre-Show Check
        return <CompletionStep />;
      default:
        return (
          <ProfileConfig
            firstName={state.firstName}
            lastName={state.lastName}
            email={state.email}
            phone={state.phone}
          />
        );
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 0: return "Crew Info";
      case 1: return "Rate Sheet";
      case 2: return "Overtime & Surcharges";
      case 3: return "Travel & Expenses";
      case 4: return "Invoicing";
      case 5: return "Pre-Show Check";
      default: return "Setup Your Account";
    }
  };

  const getStepSubtitle = () => {
    switch (currentStep) {
      case 0: return "Enter your contact details";
      case 1: return "Set your standard rates — adjust per client or shift when terms differ";
      case 2: return "Configure overtime multipliers, surcharges, and premiums";
      case 3: return "Configure travel policies and expense reimbursement";
      case 4: return "Set up your default invoice preferences";
      case 5: return "Review your configuration before going live";
      default: return "Complete these steps to get started";
    }
  };

  // Click-time validation: imperative handle checks for unsaved inline edits.
  // Schema-level state validation is handled by ValidationProvider in OnboardingLayout.
  const validateCurrentStep = () => {
    if (currentStep === 1 && rolesRef.current) {
      return rolesRef.current.isStepValid();
    }
    if (currentStep === 2 && multipliersRef.current) {
      return multipliersRef.current.isStepValid();
    }
    return true;
  };

  // Imperative-handle validity (unsaved inline edits) — passed to layout
  // and combined with schema validation there.
  const handleStepValid =
    currentStep === 2 ? multipliersStepValid
    : currentStep === 1 ? rolesStepValid
    : true;

  return (
    <OnboardingLayout
      title={getStepTitle()}
      subtitle={getStepSubtitle()}
      steps={steps}
      onNext={validateCurrentStep}
      isLastStep={currentStep === steps.length - 1}
      externalStepValid={handleStepValid}
      navigationHint={
        currentStep === 1 && !rolesStepValid
          ? "Add at least one role with a rate to continue."
          : currentStep === 2
            ? "These settings are optional — click Next to skip."
            : undefined
      }
    >
      {renderStep()}
    </OnboardingLayout>
  );
};

const OnboardingPage = () => {
  const { user, isLoading, hasProfile } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (hasProfile) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <OnboardingConfigProvider isOnboarding>
      <OnboardingWizardProvider>
        <OnboardingContent />
      </OnboardingWizardProvider>
    </OnboardingConfigProvider>
  );
};

export default OnboardingPage;
